import { useEffect, useState } from "react";
import {
  FormControl,
  FormLabel,
  Input,
  Button,
  HStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Box,
  Text,
  VStack,
  Checkbox,
  useDisclosure,
  Divider,
  Badge,
  Textarea,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionIcon,
  AccordionPanel
} from "@chakra-ui/react";

const mdmLevels = ["High", "Moderate", "Low", "Minimal"];

const mdmOptions = {
  Minimal: ["1 self-limited or minor problem"],
  Low: [
    "2 or more self-limited or minor problems",
    "1 stable chronic illness",
    "1 acute, uncomplicated illness or injury",
  ],
  Moderate: [
    "1 or more chronic illnesses with exacerbation, progression, or side effects of treatment",
    "2 or more stable chronic illnesses",
    "1 undiagnosed new problem with uncertain prognosis",
    "1 acute illness with systemic symptoms",
    "1 acute complicated injury",
  ],
  High: [
    "1 or more chronic illnesses with severe exacerbation, progression, or side effects of treatment",
    "1 acute or chronic illness or injury that poses a threat to life or bodily function",
  ],
};

export default function ProblemAddressedSelector({ id, value, onChange }) {
  const [selectedOptions, setSelectedOptions] = useState({});
  const [inputValue, setInputValue] = useState(value || "");
  const [previewText, setPreviewText] = useState("");

  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  // Update preview when selections change (only if modal is open)
  useEffect(() => {
    if (isOpen && hasSelections()) {
      const structuredText = generateStructuredText();
      if (structuredText) {
        setPreviewText(structuredText);
      }
    }
  }, [selectedOptions, isOpen]);

  // When modal opens, set preview to current input value
  useEffect(() => {
    if (isOpen) {
      setPreviewText(inputValue);
    }
  }, [isOpen, inputValue]);

  const handleOptionChange = (level, item) => {
    setSelectedOptions((prev) => {
      const current = prev[level] || [];
      const updated = current.includes(item)
        ? current.filter((opt) => opt !== item)
        : [...current, item];

      return updated.length > 0
        ? { ...prev, [level]: updated }
        : { ...prev, [level]: [] };
    });
  };

  // Helper function to generate structured text from selections
  const generateStructuredText = () => {
    const selectedText = [];

    mdmLevels.forEach(level => {
      const levelOptions = selectedOptions[level] || [];
      if (levelOptions.length > 0) {
        selectedText.push(`${level}: [${levelOptions.join("; ")}]`);
      }
    });

    return selectedText.join(", ");
  };

  const handleApply = () => {
    // Apply exactly what's in the preview
    const finalValue = previewText.trim();
    setInputValue(finalValue);
    onChange?.(finalValue);
    onClose();
  };

  const handleClear = () => {
    setSelectedOptions({});
    setPreviewText("");
  };

  const handleModalClose = () => {
    // Clear selections when closing/canceling to reset state for next time
    setSelectedOptions({});
    onClose();
  };

  const hasSelections = () => {
    return Object.values(selectedOptions).some(level => level && level.length > 0);
  };

  const renderLevel = (level) => {
    const options = mdmOptions[level];
    const selectedItems = selectedOptions[level] || [];

    return (
      <Box key={level} mb={6}>
        <HStack justify="space-between" mb={3}>
          <Text fontWeight="bold" fontSize="lg" color="blue.600">
            {level}
          </Text>
          {selectedItems.length > 0 && (
            <Badge colorScheme="blue" variant="subtle">
              {selectedItems.length} selected
            </Badge>
          )}
        </HStack>
        <VStack align="start" spacing={2} pl={2}>
          {options.map((item) => (
            <Checkbox
              key={item}
              isChecked={selectedItems.includes(item)}
              onChange={() => handleOptionChange(level, item)}
              colorScheme="blue"
            >
              <Text fontSize="sm">{item}</Text>
            </Checkbox>
          ))}
        </VStack>
      </Box>
    );
  };

  return (
    <FormControl>
      <FormLabel>No. of problems addressed</FormLabel>
      <HStack spacing={2} align="center">
        <Input
          id={id}
          placeholder="Type or select options..."
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            onChange?.(e.target.value);
          }}
        />
        <Button size="sm" variant="outline" onClick={onOpen}>
          Select
        </Button>
      </HStack>

      <Modal isOpen={isOpen} onClose={handleModalClose} size="xl" scrollBehavior="inside">
        <ModalOverlay bg="blackAlpha.300" />
        <ModalContent maxH="85%" maxW="75%" w="auto" minW="40%" overflowY="auto" overflowX="hidden" borderRadius="md">
          <ModalHeader>Select Problem Complexity Options</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {mdmLevels.map((level, index) => (
              <Box key={level}>
                {renderLevel(level)}
                {index !== mdmLevels.length - 1 && <Divider my={4} />}
              </Box>
            ))}

            <Divider my={4} />
            <Accordion allowToggle mb={3}>
              <AccordionItem border="none">
                <h2>
                  <AccordionButton px={0} py={1} _hover={{ bg: "transparent" }} display="inline-flex" alignItems="center">
                    <Text fontSize="sm" fontWeight="semibold" color="gray.700" mr={2}>
                      Instructions
                    </Text>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel px={0} pt={2} pb={1} fontSize="sm" color="gray.600" lineHeight="1.7">
                  • Use the options above to generate structured text automatically.<br />
                  • The result appears below in the <b>Preview</b> section and can be customized manually.<br />
                  • <Text as="span" fontWeight="semibold" color="red.400">Important:</Text> Make manual changes only <i>after</i> selecting options — new selections will overwrite existing text.
                </AccordionPanel>
              </AccordionItem>
            </Accordion>

            {/* Editable Live Preview Box */}
            <Box>
              <HStack justify="space-between" mb={3}>
                <Text fontWeight="bold" fontSize="lg" color="green.600">
                  Preview
                </Text>
                <HStack spacing={2}>
                  <Badge colorScheme="gray" variant="subtle" fontSize="xs">
                    Editable
                  </Badge>
                  {hasSelections() && (
                    <Button
                      size="xs"
                      variant="ghost"
                      colorScheme="blue"
                      onClick={() => setPreviewText(generateStructuredText())}
                    >
                      Reset to Selected
                    </Button>
                  )}
                </HStack>
              </HStack>
              <Box
                border="1px"
                borderColor="gray.200"
                borderRadius="md"
                bg="green.50"
                position="relative"
                _focusWithin={{
                  borderColor: "gray.400",
                  bg: "green.25"
                }}
              >
                <Textarea
                  value={previewText}
                  onChange={(e) => setPreviewText(e.target.value)}
                  placeholder="Select options above or type directly here.."
                  minH="100px"
                  maxH="200px"
                  resize="vertical"
                  border="none"
                  bg="transparent"
                  fontSize="sm"
                  lineHeight="1.6"
                  color="gray.700"
                  _focus={{
                    boxShadow: "none",
                    outline: "none"
                  }}
                  _placeholder={{
                    color: "gray.400",
                    fontStyle: "italic"
                  }}
                />
              </Box>

              <HStack justify="space-between" mt={2}>
                <Text fontSize="xs" color="gray.500">
                  Edit directly or use options above • This content will be saved exactly as shown
                </Text>
                <Text fontSize="xs" color="blue.500" fontWeight="medium">
                  {previewText ? `${previewText.length} characters` : ""}
                </Text>
              </HStack>
            </Box>
          </ModalBody>
          <ModalFooter justifyContent="space-between">
            <Button variant="ghost" colorScheme="red" onClick={handleClear}>
              Clear
            </Button>
            <HStack spacing={2}>
              <Button variant="ghost" onClick={handleModalClose}>
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={() => {
                  handleApply();
                  // Clear selections after applying to reset for next session
                  // setTimeout(() => setSelectedOptions({}), 100);
                }}
              >
                Apply
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </FormControl>
  );
}